import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch notifications
    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Skip notifications if on interview invite pages (no authentication required)
            const isOnInterviewInvitePage = window.location.pathname.includes('/interview/invite/') ||
                                           window.location.pathname.includes('/interview/session/invite/') ||
                                           window.location.pathname.includes('/interview/start');
            
            if (isOnInterviewInvitePage) {
                // Silently skip notification fetching for invite-based users
                setLoading(false);
                return;
            }
            
            const response = await api.get('/notifications/');
            setNotifications(response.data.results || response.data);
            
            // Update unread count
            const unreadResponse = await api.get('/notifications/unread_count/');
            setUnreadCount(unreadResponse.data.unread_count);
            
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch notification summary
    const fetchNotificationSummary = useCallback(async () => {
        try {
            // Skip notifications if on interview invite pages (no authentication required)
            const isOnInterviewInvitePage = window.location.pathname.includes('/interview/invite/') ||
                                           window.location.pathname.includes('/interview/session/invite/') ||
                                           window.location.pathname.includes('/interview/start');
            
            if (isOnInterviewInvitePage) {
                // Silently skip notification fetching for invite-based users
                return;
            }
            
            const response = await api.get('/notifications/summary/');
            setUnreadCount(response.data.unread_count);
            setNotifications(response.data.recent_notifications || []);
        } catch (err) {
            // Only log error if not on interview invite pages
            const isOnInterviewInvitePage = window.location.pathname.includes('/interview/invite/') ||
                                           window.location.pathname.includes('/interview/session/invite/') ||
                                           window.location.pathname.includes('/interview/start');
            
            if (!isOnInterviewInvitePage) {
                console.error('Error fetching notification summary:', err);
            }
        }
    }, []);

    // Mark notifications as read
    const markAsRead = useCallback(async (notificationIds = null, markAll = false) => {
        try {
            const payload = markAll ? { mark_all: true } : { notification_ids: notificationIds };
            await api.post('/notifications/mark_read/', payload);
            
            // Update local state
            if (markAll) {
                setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                setUnreadCount(0);
            } else if (notificationIds) {
                setNotifications(prev => 
                    prev.map(n => 
                        notificationIds.includes(n.id) ? { ...n, is_read: true } : n
                    )
                );
                setUnreadCount(prev => Math.max(0, prev - notificationIds.length));
            }
            
        } catch (err) {
            console.error('Error marking notifications as read:', err);
            throw err;
        }
    }, []);

    // Mark single notification as read
    const markSingleAsRead = useCallback(async (notificationId) => {
        try {
            await api.post(`/notifications/${notificationId}/mark_single_read/`);
            
            // Update local state
            setNotifications(prev => 
                prev.map(n => 
                    n.id === notificationId ? { ...n, is_read: true } : n
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
            
        } catch (err) {
            console.error('Error marking notification as read:', err);
            throw err;
        }
    }, []);

    // Add new notification (for real-time updates)
    const addNotification = useCallback((notification) => {
        setNotifications(prev => [notification, ...prev]);
        if (!notification.is_read) {
            setUnreadCount(prev => prev + 1);
        }
    }, []);

    // Remove notification
    const removeNotification = useCallback((notificationId) => {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
    }, []);

    // Initialize notifications on mount
    useEffect(() => {
        fetchNotificationSummary();
    }, [fetchNotificationSummary]);

    // Set up polling for new notifications (every 30 seconds)
    useEffect(() => {
        const interval = setInterval(() => {
            fetchNotificationSummary();
        }, 5*60000);
        // 60000 means 1 minute, so 5*60000 means 5 minutes 
        return () => clearInterval(interval);
    }, [fetchNotificationSummary]);

    const value = {
        notifications,
        unreadCount,
        loading,
        error,
        fetchNotifications,
        fetchNotificationSummary,
        markAsRead,
        markSingleAsRead,
        addNotification,
        removeNotification,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
